import { collection, doc, serverTimestamp } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { recalculateElementProgressTree } from '../../services/cascadeEngine'
import { checkChecklistsSubtreeRoom, runChecklistCreateTransaction } from '../../services/checklistCounterTransaction'
import type { ChecklistItem as BoardChecklistItem, TimelineBoardKey } from '../../types/schema'
import type { useWorkspace } from '../../context/useWorkspace'
import { createStripboardRecordDefaults } from '../../utils/stripboardDefaults'
import { findEmptyCanvasSlot, toSlotBox, type CanvasSlotBox } from '../../services/canvasPlacementService'
import {
  GRID_LINE_HEIGHT,
  NODE_GAP,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import {
  CHECKLIST_CANVAS_LAYOUT_VERSION as WORKSPACE_CANVAS_LAYOUT_VERSION,
} from '../../types/checklistCanvas'
import {
  normalizeWeightage,
  snapYToGrid,
} from './boardItem'

export interface UseAutoSpawnTemplatesParams {
  displayNodes: BoardChecklistItem[]
  item: { board?: string }
  activeWorkspace: ReturnType<typeof useWorkspace>['workspaces'][number] | undefined
  collectPlacementBoxes: () => CanvasSlotBox[]
  currentUser: { uid?: string; email?: string | null } | null
  currentDisplayName: string
  showFlash: (message: string) => void
}

export function useAutoSpawnTemplates({
  displayNodes,
  item,
  activeWorkspace,
  collectPlacementBoxes,
  currentUser,
  currentDisplayName,
  showFlash,
}: UseAutoSpawnTemplatesParams) {
  const spawnAutoTemplateNodes = async (sourceNode: BoardChecklistItem) => {
    const templates = sourceNode.hasAutoSpawnTrigger ? sourceNode.autoSpawnTemplates ?? [] : []
    const usableTemplates = templates
      .map((template) => ({
        title: template.title.trim(),
        weightage: normalizeWeightage(template.weightage),
        relativeYOffset: typeof template.relativeYOffset === 'number' && Number.isFinite(template.relativeYOffset)
          ? template.relativeYOffset
          : GRID_LINE_HEIGHT,
      }))
      .filter((template) => template.title)

    if (usableTemplates.length === 0) return

    const alreadySpawnedTitles = new Set(
      displayNodes
        .filter((candidate) => candidate.prerequisites.includes(sourceNode.id))
        .map((candidate) => candidate.title.trim().toLowerCase()),
    )
    const templatesToSpawn = usableTemplates.filter(
      (template) => !alreadySpawnedTitles.has(template.title.toLowerCase()),
    )
    if (templatesToSpawn.length === 0) return

    const resolvedBoard = (item.board ?? 'DASHBOARD') as TimelineBoardKey
    const roomCheck = await checkChecklistsSubtreeRoom(firestoreDb, {
      workspaceId: sourceNode.workspaceId,
      board: resolvedBoard,
      parentContainerId: sourceNode.parentContainerId,
      subtreeSize: templatesToSpawn.length,
      fallbackLimits: activeWorkspace?.workspaceLimits,
    })
    if (!roomCheck.ok) {
      showFlash(roomCheck.message ?? 'Checklist limit reached.')
      return
    }

    // Track placement against the full book layout, appending each just-
    // spawned template so back-to-back spawns don't collide with each other.
    const growingExisting: CanvasSlotBox[] = collectPlacementBoxes()
    let createdCount = 0

    for (const [index, template] of templatesToSpawn.entries()) {
      const checklistRef = doc(collection(firestoreDb, 'checklists'))
      // Preferred slot: to the right of the source, at the template's
      // requested Y offset. `findEmptyCanvasSlot` snaps to the nearest
      // empty cell if that lands on top of anything.
      const preferredY = snapYToGrid(sourceNode.position.y + template.relativeYOffset)
      const preferredX = Math.max(0, sourceNode.position.x + ((index + 1) * (NODE_WIDTH + NODE_GAP)))
      const spawnPosition = findEmptyCanvasSlot({
        existing: growingExisting,
        newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
        preferred: { x: preferredX, y: preferredY },
      })
      growingExisting.push(toSlotBox({ position: spawnPosition, nodeKind: 'CHECKLIST' }))

      const nextNode: BoardChecklistItem = {
        ...createStripboardRecordDefaults({ boardStatus: 'TODO' }),
        id: checklistRef.id,
        parentContainerId: sourceNode.parentContainerId,
        workspaceId: sourceNode.workspaceId,
        title: template.title,
        description: '',
        completionCriteria: '',
        createdById: currentUser?.uid ?? activeWorkspace?.ownerId ?? '',
        createdByName: currentDisplayName || currentUser?.email || 'Workspace admin',
        createdAt: new Date().toISOString(),
        assigneeId: null,
        assigneeName: null,
        workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
        isCompleted: false,
        weightage: template.weightage,
        position: spawnPosition,
        prerequisites: [],
        blockedByExternal: null,
        blockedByExternalId: null,
        interWorkspaceTargetId: null,
        isHelperVisible: true,
        dependencyLogicGate: 'ALL_PREREQUISITES_MET',
        hasAutoSpawnTrigger: false,
        autoSpawnTemplates: [],
        requiresProofFile: false,
        proofFileUrl: null,
        proofFileName: null,
        requiresCoSign: false,
        coSignerRoleTarget: 'ADMIN',
        coSignedByUid: null,
        coSignedByName: null,
        isPendingReview: false,
        requiresLocationPin: false,
        targetCoordinates: null,
        verifiedCoordinates: null,
        proofVideoUrl: null,
        countdownHours: null,
        timerStartedAt: null,
        timerExpiredAt: null,
        isUrgentCrisisMode: false,
        enforcePaintSpringDelay: false,
        delayDurationHours: 0,
        commentsCount: 0,
        voiceMemoBlobUrl: null,
        isDescriptionPrivate: false,
        estimatedHours: null,
        actualCost: null,
        complexityStars: 1,
        urgencyStars: 1,
        importanceStars: 1,
        currencyCode: 'USD',
        blockedByNodeIds: [],
        isBlockerForNodeIds: [],
      }

      const persistedPayload = {
        ...nextNode,
        sourceChecklistId: checklistRef.id,
        board: resolvedBoard,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      await runChecklistCreateTransaction(firestoreDb, {
        workspaceId: sourceNode.workspaceId,
        board: resolvedBoard,
        parentContainerId: sourceNode.parentContainerId,
        checklistRef,
        checklistData: persistedPayload,
        fallbackLimits: activeWorkspace?.workspaceLimits,
      })
      createdCount += 1
    }

    if (createdCount === 0) return
    await recalculateElementProgressTree(firestoreDb, sourceNode.parentContainerId)
    showFlash(`Auto-spawned ${createdCount} follow-up node${createdCount === 1 ? '' : 's'}.`)
  }

  return {
    spawnAutoTemplateNodes,
  }
}
